import { Injectable, inject } from '@angular/core';
import {
  EffectivePermissionsDetailsResponse,
  EffectivePermissionsResponse,
  EntityApplications,
  EntityPermissionActivityKeys,
  UserRoleAssignmentBulkDetailsResponse,
  UserRoleAssignmentBulkRequest,
  UserRoleAssignmentBulkResponse,
  UserRoleAssignmentGetListResponse,
  UserRoleAssignmentListItemResponse,
  UserRoleDetailsResponse,
  UserRoleGetListResponse,
  UserRoleListItemResponse,
  UserRoleResponse,
  UserRoleUpsertRequest,
} from '@ag-common-lib/public-api';
import { LoadOptions } from 'devextreme/common/data';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { ReportingApiService } from './reporting-api.service';

const ASSIGNMENTS_GRID_PAGE_SIZE = 50;

/**
 * Client for the identity API — roles, assignments and effective permissions.
 *
 * Everything authorization-related now comes from here rather than from Firestore
 * documents, so the server stays the only place that decides what a role grants.
 */
@Injectable({ providedIn: 'root' })
export class IdentityApiService {
  private readonly reportingApiService = inject(ReportingApiService);

  private readonly basePath = '/api/identity';

  /**
   * Effective permissions for the signed-in caller, already OR-merged server-side.
   * Resolves the impersonated agent when a session is impersonating, matching what
   * every other endpoint does.
   */
  getMyPermissions(appId: EntityApplications): Promise<EffectivePermissionsResponse> {
    return this.reportingApiService
      .request<EffectivePermissionsDetailsResponse>(`${this.basePath}/me/permissions`, {
        query: { appId },
      })
      .then(response => response.data);
  }

  /** For the portal's viewed-agent case. Gated server-side by the impersonation permission. */
  getAgentPermissions(agentFirebaseDbId: string, appId: EntityApplications): Promise<EffectivePermissionsResponse> {
    return this.reportingApiService
      .request<EffectivePermissionsDetailsResponse>(
        `${this.basePath}/agents/${encodeURIComponent(agentFirebaseDbId)}/permissions`,
        { query: { appId } },
      )
      .then(response => response.data);
  }

  /**
   * The agent-side companion to `getRoles(entity, activity)`: which agents hold that
   * permission. Replaces resolving role ids and then querying the Firestore agent
   * documents by their `roles` array, which stopped moving once assignments became
   * SQL-owned.
   */
  getAgentsByPermission(
    appId: EntityApplications,
    entity: string,
    activity: EntityPermissionActivityKeys = EntityPermissionActivityKeys.read,
  ): Promise<UserRoleAssignmentListItemResponse[]> {
    return this.reportingApiService
      .request<UserRoleAssignmentGetListResponse>(`${this.basePath}/agents/by-permission`, {
        query: { appId, entity, activity },
      })
      .then(response => response.data ?? []);
  }

  /**
   * `entity` + `activity` ask the reverse question — which roles grant this — and are
   * answered by a jsonb containment lookup rather than by returning everything.
   */
  getRoles(
    appId?: EntityApplications,
    includeDisabled = true,
    entity?: string,
    activity?: string,
  ): Promise<UserRoleListItemResponse[]> {
    return this.reportingApiService
      .request<UserRoleGetListResponse>(`${this.basePath}/roles`, {
        query: {
          ...(appId ? { appId } : {}),
          ...(entity && activity ? { entity, activity } : {}),
          includeDisabled,
        },
      })
      .then(response => response.data ?? []);
  }

  /**
   * The caller's own roles across every application, unfiltered. Each app applies its
   * own `isEnable` / `appId` filter, exactly as it did against the Firestore
   * collection.
   */
  getMyRoles(): Promise<UserRoleListItemResponse[]> {
    return this.reportingApiService
      .request<UserRoleGetListResponse>(`${this.basePath}/me/roles`)
      .then(response => response.data ?? []);
  }

  getRole(externalId: string): Promise<UserRoleResponse> {
    return this.reportingApiService
      .request<UserRoleDetailsResponse>(`${this.basePath}/roles/${encodeURIComponent(externalId)}`)
      .then(response => response.data);
  }

  createRole(body: UserRoleUpsertRequest): Promise<UserRoleResponse> {
    return this.reportingApiService
      .request<UserRoleDetailsResponse>(`${this.basePath}/roles`, { method: 'POST', body })
      .then(response => response.data);
  }

  /**
   * `activity` merges into the stored map by default, so the editor can keep saving
   * one toggled entity at a time without wiping the rest of the role.
   */
  updateRole(
    externalId: string,
    body: {
      role?: string;
      activity?: Record<string, Record<string, boolean>>;
      isEnable?: boolean;
      assignmentTrigger?: string;
      mergeActivity?: boolean;
    },
  ): Promise<UserRoleResponse> {
    return this.reportingApiService
      .request<UserRoleDetailsResponse>(`${this.basePath}/roles/${encodeURIComponent(externalId)}`, {
        method: 'PUT',
        body,
      })
      .then(response => response.data);
  }

  /** Disables the role; the row survives because other documents reference its id. */
  disableRole(externalId: string): Promise<UserRoleResponse> {
    return this.reportingApiService
      .request<UserRoleDetailsResponse>(`${this.basePath}/roles/${encodeURIComponent(externalId)}`, {
        method: 'DELETE',
      })
      .then(response => response.data);
  }

  /**
   * Assignment rows for a known set of agents.
   *
   * The conference and form-management ACL grids list the agents already granted a
   * conference or form, and were reading each one's roles from the Firestore agent
   * document — the array that stopped being written at the SQL cutover. The row this
   * returns carries the same name, email and role fields those grids already bind.
   */
  getAssignmentsByAgentIds(
    agentFirebaseDbIds: readonly string[],
  ): Promise<UserRoleAssignmentListItemResponse[]> {
    if (!agentFirebaseDbIds.length) {
      return Promise.resolve([]);
    }

    return this.getAssignments({
      filter: ['dbId', 'anyof', [...agentFirebaseDbIds]],
      take: agentFirebaseDbIds.length,
      requireTotalCount: false,
    }).then(response => response.data ?? []);
  }

  getAssignments(loadOptions: LoadOptions | Record<string, unknown> = {}): Promise<UserRoleAssignmentGetListResponse> {
    return this.reportingApiService.request<UserRoleAssignmentGetListResponse>(
      `${this.basePath}/role-assignments/get-list`,
      { method: 'POST', body: { loadOptions } },
    );
  }

  /**
   * One call, one transaction, one set-based statement.
   *
   * The screen this replaces issued a write per agent and swallowed rejections, so a
   * half-applied mass assignment was indistinguishable from a successful one.
   *
   * Target either by `agentFirebaseDbIds` or by `filter` — never both. The filter
   * form matters because the grid is server-side paged: "select all matching" can
   * span agents the browser has never loaded, so it sends the predicate instead of
   * an id list it cannot produce. Pass `expectedAgentCount` alongside it and the
   * write aborts if the selection has grown since it was previewed.
   */
  applyAssignments(body: UserRoleAssignmentBulkRequest): Promise<UserRoleAssignmentBulkResponse> {
    return this.reportingApiService
      .request<UserRoleAssignmentBulkDetailsResponse>(`${this.basePath}/role-assignments/bulk`, {
        method: 'POST',
        body,
      })
      .then(response => response.data);
  }

  createAssignmentsDataSource(): DataSource<UserRoleAssignmentListItemResponse, string> {
    return new DataSource({
      paginate: true,
      pageSize: ASSIGNMENTS_GRID_PAGE_SIZE,
      store: new CustomStore({
        /**
         * `satisfies` ties the key to the response contract: the row is keyed by the
         * AgentKeys the grid template binds, and if that contract is ever rekeyed
         * again this stops compiling instead of failing at runtime with DevExtreme
         * E1046 ("key field is not found in data objects").
         */
        key: 'dbId' satisfies keyof UserRoleAssignmentListItemResponse,
        load: async (loadOptions: LoadOptions) => {
          const response = await this.getAssignments(loadOptions);

          return { data: response.data ?? [], totalCount: response.totalCount ?? 0 };
        },
      }),
    });
  }
}
